import { useCallback } from 'react';

import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';

import { type AvatarData } from '@/api';
import { useUser } from '@/lib/auth';
import { normalizeQuizAvatarData } from '@/shared/utils';

import {
  type AvatarEventSource,
  type AvatarLoginState,
} from '../events.quiz-profile-avatar';

import { type AvatarSaveStrategy } from './types';

const AVATAR_PART_KEYS = ['skinId', 'faceId', 'hairId', 'clothesId'] as const;

function isSelectedAccessoryId(id: unknown) {
  return typeof id === 'number' && Number.isFinite(id) && id > 0;
}

function getAccessoryCount(avatar: AvatarData) {
  return Object.values(avatar.accessoryIds ?? {}).filter(isSelectedAccessoryId)
    .length;
}

function serializeAccessoryIds(avatar: AvatarData | null | undefined) {
  return Object.entries(avatar?.accessoryIds ?? {})
    .filter(([, id]) => isSelectedAccessoryId(id))
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([slot, id]) => `${slot}:${id}`)
    .join(',');
}

function getChangedFields(
  before: AvatarData | null | undefined,
  after: AvatarData,
) {
  const normalizedBefore =
    before !== null && before !== undefined
      ? normalizeQuizAvatarData(before)
      : before;

  const changedFields = AVATAR_PART_KEYS.filter(
    key => normalizedBefore?.[key] !== after[key],
  ).map(key => key.replace('Id', '').toLowerCase());

  if (
    serializeAccessoryIds(normalizedBefore) !== serializeAccessoryIds(after)
  ) {
    changedFields.push('accessories');
  }

  return changedFields;
}

function getSaveReason(error: unknown) {
  if (error instanceof Error && error.name) return error.name;
  return 'unknown';
}

type BuildAvatarSaveEventInput = {
  result: 'success' | 'fail';
  reason?: string;
  source: AvatarEventSource;
  login_state: AvatarLoginState;
  before: AvatarData | null | undefined;
  after: AvatarData;
};

export function buildAvatarSaveEvent({
  result,
  reason,
  source,
  login_state,
  before,
  after,
}: BuildAvatarSaveEventInput) {
  const normalizedBefore =
    before !== null && before !== undefined
      ? normalizeQuizAvatarData(before)
      : before;
  const normalizedAfter = normalizeQuizAvatarData(after);
  const changedFields = getChangedFields(normalizedBefore, normalizedAfter);

  return {
    result,
    ...(reason !== undefined && { reason }),
    source,
    login_state,
    changed_count: changedFields.length,
    changed_fields: changedFields.join(','),
    ...(normalizedBefore?.skinId !== undefined && {
      before_skin_id: normalizedBefore.skinId,
    }),
    ...(normalizedBefore?.faceId !== undefined && {
      before_face_id: normalizedBefore.faceId,
    }),
    ...(normalizedBefore?.hairId !== undefined && {
      before_hair_id: normalizedBefore.hairId,
    }),
    ...(normalizedBefore?.clothesId !== undefined && {
      before_clothes_id: normalizedBefore.clothesId,
    }),
    ...(normalizedAfter.skinId !== undefined && {
      after_skin_id: normalizedAfter.skinId,
    }),
    ...(normalizedAfter.faceId !== undefined && {
      after_face_id: normalizedAfter.faceId,
    }),
    ...(normalizedAfter.hairId !== undefined && {
      after_hair_id: normalizedAfter.hairId,
    }),
    ...(normalizedAfter.clothesId !== undefined && {
      after_clothes_id: normalizedAfter.clothesId,
    }),
    before_accessory_ids: serializeAccessoryIds(normalizedBefore),
    after_accessory_ids: serializeAccessoryIds(normalizedAfter),
    before_selected_accessory_count: normalizedBefore
      ? getAccessoryCount(normalizedBefore)
      : 0,
    after_selected_accessory_count: getAccessoryCount(normalizedAfter),
    selected_accessory_count: getAccessoryCount(normalizedAfter),
  };
}

export function useTrackedAvatarSave(
  save: AvatarSaveStrategy['save'],
  source: AvatarEventSource,
  before: AvatarData | null | undefined,
) {
  const { isAuthenticated } = useUser();
  const login_state: AvatarLoginState = isAuthenticated
    ? 'authenticated'
    : 'guest';

  return useCallback<AvatarSaveStrategy['save']>(
    async (after, name) => {
      // 트래킹 실패가 저장 흐름(모달 닫힘·원본 에러)을 삼키지 않게 격리한다.
      const trackSave = (event: BuildAvatarSaveEventInput) => {
        try {
          trackEvent('quiz_profile_avatar_save', buildAvatarSaveEvent(event));
        } catch (error) {
          logger.captureError(error);
        }
      };

      try {
        await save(after, name);
      } catch (error) {
        trackSave({
          result: 'fail',
          reason: getSaveReason(error),
          source,
          login_state,
          before,
          after,
        });
        throw error;
      }

      trackSave({ result: 'success', source, login_state, before, after });
    },
    [before, login_state, save, source],
  );
}
